/**
 * Fire-and-forget client beacons for the hero CTA funnel (Wave H0):
 * `hero_cta_view` (once per surface at ≥50% visibility) and `hero_cta_click`
 * (every action). Same shape as track-upgrade-cta.ts — sendBeacon survives
 * the navigation an `sms:`/`wa.me` click triggers, the route is allow-listed
 * for anonymous senders + fail-open + always 200, so this can never surface
 * an error or block the tap. Nothing identifying travels: the anon cookie id
 * and the attribution ref are the only keys.
 */

import type { SiteLocale } from '@/lib/marketing/page-metadata';
import type {
  HeroCtaChannel,
  HeroCtaDevice,
  HeroCtaSurface,
  HeroCtaVariant,
  HeroCtaVariantSource,
} from '@/lib/onboarding/hero-cta';

export interface HeroCtaEventContext {
  variant: HeroCtaVariant;
  variant_source: HeroCtaVariantSource;
  device: HeroCtaDevice;
  surface: HeroCtaSurface;
  locale: SiteLocale;
  aid: string | null;
  ref: string | null;
}

export function trackHeroCtaView(ctx: HeroCtaEventContext): void {
  send({ eventType: 'hero_cta_view', ...compact(ctx) });
}

export function trackHeroCtaClick(ctx: HeroCtaEventContext, channel: HeroCtaChannel): void {
  send({ eventType: 'hero_cta_click', ...compact(ctx), channel });
}

/** Drop nulls so the route's per-key whitelist sees only real values. */
function compact(ctx: HeroCtaEventContext): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(ctx)) {
    if (typeof v === 'string' && v.length > 0) out[k] = v;
  }
  return out;
}

function send(payload: Record<string, string>): void {
  try {
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(
        '/api/telemetry/product-event',
        new Blob([body], { type: 'application/json' }),
      );
      return;
    }
    void fetch('/api/telemetry/product-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // telemetry never throws into UI code
  }
}
